import { useEffect, useRef, useState, type FormEvent } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import {
  Check,
  ExternalLink,
  MapPin,
  QrCode,
  Share2,
  Trash2,
} from 'lucide-react'
import { Button, ButtonLink, Collapsible, ConfirmDialog, CopyField, Drawer, IconButton, Notice, Skeleton, SkeletonGroup, Switch, TextField, copyText } from '@/components/play'
import { api } from '@/util/api'
import { workspaceError } from '@/lib/workspace'
import { useProductEvent } from '@/hooks/useProductEvent'
import { CameraStatus, useCameraState } from './WorkspaceCamera'
import type { StreamResource } from '@/types/events'

export function CameraInvitePanel({
  open,
  onOpenChange,
  eventId,
  eventName,
  stream,
  onSaved,
  onDeleted,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  eventId: string
  eventName: string
  stream: StreamResource | null
  onSaved: (stream: StreamResource) => void
  onDeleted: (id: string) => void
}) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [removeError, setRemoveError] = useState('')
  const nameField = useRef<HTMLInputElement>(null)
  useEffect(() => {
    setName(stream?.attributes.name || '')
    setError('')
  }, [open, stream?.id])
  async function save(event: FormEvent) {
    event.preventDefault()
    if (busy || !name.trim()) return
    setBusy(true)
    setError('')
    try {
      const response = stream
        ? await api.patch<{ data: StreamResource }>(`/streams/${stream.id}`, {
            stream: { name: name.trim() },
          })
        : await api.post<{ data: StreamResource }>('/streams', {
            stream: {
              name: name.trim(),
              event_id: eventId,
              protocol: 'srt',
              auto_start: false,
            },
          })
      onSaved(response.data)
    } catch (failure) {
      setError(
        workspaceError(failure, 'The camera couldn’t be saved. Try again.'),
      )
    } finally {
      setBusy(false)
    }
  }
  // Confirmed in a ConfirmDialog, which stays open and loading until this settles and keeps a failure in view.
  async function remove() {
    if (!stream) return
    setBusy(true)
    setRemoveError('')
    try {
      await api.delete(`/streams/${stream.id}`)
      onDeleted(stream.id)
      onOpenChange(false)
    } catch (failure) {
      setRemoveError(
        workspaceError(failure, 'The camera couldn’t be removed. Try again.'),
      )
      throw failure
    } finally {
      setBusy(false)
    }
  }
  return (
    <Drawer
      open={open}
      onOpenChange={(value, details) => {
        if (busy) details.cancel()
        else onOpenChange(value)
      }}
      // A new camera starts at its name; an existing invitation opens on the panel itself.
      initialFocus={stream ? undefined : nameField}
      eyebrow={eventName}
      title={stream ? 'Camera invitation' : 'Add a camera'}
      description={stream ? undefined : 'Give this view a name your crew will recognize.'}
      className="pw-panel ph-no-capture ph-no-record"
    >
      <form className="pw-form" onSubmit={save}>
        <div className="flex items-end gap-xs">
          <TextField
            ref={nameField}
            label="Camera name"
            className="min-w-0 flex-1"
            maxLength={160}
            required
            placeholder="e.g. Front pack or Finish line"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={busy}
          />
          {stream && name.trim() !== stream.attributes.name && (
            <IconButton
              type="submit"
              label="Save camera name"
              variant="ghost"
              disabled={busy || !name.trim()}
            >
              <Check />
            </IconButton>
          )}
        </div>
        {!stream && (
          <Button type="submit" loading={busy} disabled={!name.trim()}>
            {busy ? 'Creating invitation' : 'Create invitation'}
            <QrCode />
          </Button>
        )}
      </form>
      {error && (
        <Notice tone="error" alert>{error}</Notice>
      )}
      {stream && (
        <CameraInvitation
          key={stream.id}
          stream={stream}
          eventName={eventName}
          onSaved={onSaved}
        />
      )}
      {stream && (
        <div className="pw-danger-zone">
          <ConfirmDialog
            trigger={
              <IconButton
                label="Remove camera"
                variant="ghost"
                onClick={() => setRemoveError('')}
              >
                <Trash2 />
              </IconButton>
            }
            title={`Remove “${stream.attributes.name}”?`}
            description="Its invitation and live connection will stop working."
            confirmLabel="Remove camera"
            onConfirm={remove}
            failureMessage={removeError || undefined}
          />
        </div>
      )}
    </Drawer>
  )
}

function CameraInvitation({
  stream,
  eventName,
  onSaved,
}: {
  stream: StreamResource
  eventName: string
  onSaved: (stream: StreamResource) => void
}) {
  const track = useProductEvent(stream.attributes.event_id || undefined)
  const [url, setUrl] = useState('')
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [copied, setCopied] = useState(false)
  const [lat, setLat] = useState(
    stream.attributes.pinned_latitude?.toString() || '',
  )
  const [lng, setLng] = useState(
    stream.attributes.pinned_longitude?.toString() || '',
  )
  const [saving, setSaving] = useState(false)
  // Shown on whichever coordinate is missing, so the field says what to fix.
  const [pinError, setPinError] = useState('')
  const state = useCameraState(stream)
  useEffect(() => {
    let cancelled = false
    setUrl('')
    setError('')
    api
      .get<{ data: { attributes: { url: string } } }>(
        `/public/streams/${stream.id}/invite-url`,
      )
      .then((response) => {
        if (!cancelled) setUrl(response.data.attributes.url)
      })
      .catch((failure) => {
        if (!cancelled)
          setError(
            workspaceError(
              failure,
              'The invitation couldn’t load. Try again.',
            ),
          )
      })
    return () => {
      cancelled = true
    }
  }, [stream.id, attempt])
  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2500)
    return () => clearTimeout(timer)
  }, [copied])
  // Without native sharing, Share invitation copies the link instead.
  async function copyInvite() {
    if (await copyText(url)) {
      setCopied(true)
      track('camera_invite_copied', { camera_id: stream.id })
    } else {
      setError('Copy did not work. Select the invitation and copy it manually.')
    }
  }
  async function share() {
    if (!navigator.share) return copyInvite()
    try {
      await navigator.share({
        title: `${stream.attributes.name} · ${eventName}`,
        text: `Join ${stream.attributes.name} on StreamBridge.`,
        url,
      })
      track('camera_invite_shared', { camera_id: stream.id })
    } catch (failure) {
      if ((failure as Error).name !== 'AbortError')
        setError('Sharing did not work. Copy the invitation link instead.')
    }
  }
  async function update(attributes: Record<string, unknown>) {
    setSaving(true)
    setError('')
    try {
      const result = await api.patch<{ data: StreamResource }>(
        `/streams/${stream.id}`,
        { stream: attributes },
      )
      onSaved(result.data)
    } catch (failure) {
      setError(
        workspaceError(failure, 'The camera couldn’t be updated. Try again.'),
      )
    } finally {
      setSaving(false)
    }
  }
  const ingress = stream.attributes.connection_details?.external_ingress_url
  const egress = stream.attributes.connection_details?.external_egress_url
  const rtmpServer = stream.attributes.connection_details?.rtmp_server_url
  return (
    <div className="pw-invitation">
      <div className="pw-invitation-state">
        <CameraStatus state={state} />
        <span>
          {state === 'live'
            ? 'Your camera is here.'
            : 'One camera. One invitation.'}
        </span>
      </div>
      {url ? (
        <>
          <div className="pw-qr">
            <QRCodeSVG
              value={url}
              size={200}
              level="M"
              marginSize={3}
              title={`Invitation for ${stream.attributes.name}`}
            />
          </div>
          <h3>Scan. Join. Be there.</h3>
          <p>
            Share this invitation with the person on this camera. They can open
            it on their phone or scan it in the StreamBridge app.
          </p>
          <CopyField
            label="Camera invitation"
            value={url}
            onCopy={(copiedInvite) => {
              if (copiedInvite) track('camera_invite_copied', { camera_id: stream.id })
            }}
          />
          <Button onClick={share}>
            {copied ? 'Invitation copied' : 'Share invitation'}
            <Share2 />
          </Button>
          <ButtonLink
            variant="ghost"
            className="pw-invitation-link"
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            Open joining instructions
            <ExternalLink aria-hidden="true" />
          </ButtonLink>
        </>
      ) : (
        !error && (
          <SkeletonGroup label="Preparing invitation" className="pw-qr-skeleton">
            <Skeleton shape="panel" className="pw-qr" />
          </SkeletonGroup>
        )
      )}
      {error && (
        <Notice
          tone="error"
          alert
          action={!url ? <Button variant="secondary" size="compact" onClick={() => setAttempt((value) => value + 1)}>Try again</Button> : undefined}
        >
          {error}
        </Notice>
      )}
      <Collapsible className="pw-details" trigger="SRT addresses">
        <p>
          Send from an SRT encoder to the input. Pull this camera into other
          systems from the output.
        </p>
        {ingress || egress ? (
          <>
            {ingress && <CopyField label="Camera input address" value={ingress} />}
            {egress && <CopyField label="Camera output address" value={egress} />}
          </>
        ) : (
          <p>
            Connection details are not available yet. Close and reopen the
            invitation to retry.
          </p>
        )}
      </Collapsible>
      {rtmpServer && (
        <Collapsible className="pw-details" trigger="RTMP address">
          <p>For a studio or encoder that only sends RTMP.</p>
          <CopyField label="Server" copyLabel="Copy RTMP server" value={rtmpServer} />
          <CopyField label="Stream key" copyLabel="Copy stream key" value={stream.attributes.stream_key} />
        </Collapsible>
      )}
      <Collapsible className="pw-details" trigger="Camera settings">
        <Switch
          label="Visible on the public event page"
          checked={stream.attributes.publicly_visible !== false}
          disabled={saving}
          onCheckedChange={(checked) => update({ publicly_visible: checked })}
        />
        <Switch
          label="Share camera audio publicly"
          checked={stream.attributes.public_audio_enabled !== false}
          disabled={saving}
          onCheckedChange={(checked) => update({ public_audio_enabled: checked })}
        />
        <form
          className="pw-form"
          onSubmit={(event) => {
            event.preventDefault()
            if ((lat === '') !== (lng === '')) {
              setPinError(
                'Enter both coordinates, or clear both to follow the camera.',
              )
              return
            }
            setPinError('')
            void update({
              pinned_latitude: lat === '' ? null : Number(lat),
              pinned_longitude: lng === '' ? null : Number(lng),
            })
          }}
        >
          <p>
            Pin a fixed camera, such as the finish line. Leave both fields empty
            to follow its live location.
          </p>
          <div className="flex gap-s">
            <TextField
              label="Latitude"
              className="min-w-0 flex-1"
              type="number"
              min={-90}
              max={90}
              step="any"
              value={lat}
              error={pinError && lat === '' ? pinError : undefined}
              onChange={(event) => {
                setLat(event.target.value)
                setPinError('')
              }}
            />
            <TextField
              label="Longitude"
              className="min-w-0 flex-1"
              type="number"
              min={-180}
              max={180}
              step="any"
              value={lng}
              error={pinError && lng === '' ? pinError : undefined}
              onChange={(event) => {
                setLng(event.target.value)
                setPinError('')
              }}
            />
          </div>
          <Button variant="secondary" disabled={saving} type="submit">
            <MapPin />
            Save location
          </Button>
        </form>
      </Collapsible>
      <span className="sr-only" role="status">
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </div>
  )
}
